import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Mic, FileAudio, Wand2, Heart } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import Testimonials from "@/components/landing/Testimonials";
import FinalCTA from "@/components/landing/FinalCTA";
import SEO from "@/components/seo/SEO";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/i18n/I18nContext";
import { useIsAndroidApp } from "@/hooks/useLaunchContext";
import { LANDING } from "@/constants/testIds";
import { formatPriceWithSign } from "@/utils/price";
import api from "@/lib/api";

export default function VoiceMessageLanding() {
  const { t, language } = useI18n();
  const isTwaApp = useIsAndroidApp();
  const isEn = language === "en";
  const [pricingConfig, setPricingConfig] = useState(null);

  useEffect(() => {
    api.get("/pricing").then(({ data }) => setPricingConfig(data));
  }, []);

  const steps = [
    {
      icon: Mic,
      title: isEn ? "Record or upload your voice" : "Enregistrez ou importez votre voix",
      desc: isEn
        ? "In your profile, record a short 10-30 second sample directly from your browser, or upload an existing audio file."
        : "Depuis votre profil, enregistrez un court échantillon de 10 à 30 secondes directement depuis votre navigateur, ou importez un fichier audio existant.",
    },
    {
      icon: Wand2,
      title: isEn ? "Our AI clones your voice" : "Notre IA clone votre voix",
      desc: isEn
        ? "Write the personal message you want to say, and our AI recreates it in your own cloned voice."
        : "Écrivez le message personnel que vous souhaitez dire, et notre IA le recrée avec votre propre voix clonée.",
    },
    {
      icon: FileAudio,
      title: isEn ? "Delivered as intro or outro" : "Livré en intro ou en outro",
      desc: isEn
        ? "Your personalized voice message is seamlessly added at the beginning, the end, or both, of your AI-generated song."
        : "Votre message vocal personnalisé est ajouté harmonieusement au début, à la fin, ou aux deux, de votre chanson générée par IA.",
    },
  ];

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: isEn ? "Personalized Voice Message (AI voice cloning)" : "Message vocal personnalisé (clonage de voix par IA)",
    description: isEn
      ? "Add a personalized spoken message in your own cloned voice as an intro or outro to your AI-generated song."
      : "Ajoutez un message vocal personnalisé, dans votre propre voix clonée, en intro ou en outro de votre chanson générée par IA.",
    provider: { "@type": "Organization", name: "Kalypso Music" },
    areaServed: "Worldwide",
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={isEn ? "Personalized Voice Message with Your Own Cloned Voice | Kalypso Music" : "Message Vocal Personnalisé avec Votre Propre Voix | Kalypso Music"}
        description={isEn
          ? "New premium option: add a personalized spoken message in your own AI-cloned voice as an intro or outro to your custom song. Unique, emotional, unforgettable."
          : "Nouvelle option premium : ajoutez un message vocal personnalisé, dans votre propre voix clonée par IA, en intro ou en outro de votre chanson sur-mesure. Unique, émouvant, inoubliable."}
        path="/message-vocal-personnalise"
        jsonLd={jsonLd}
      />
      <Navbar />
      <main>
        <section className="px-4 py-24 sm:px-6 lg:px-8" data-testid="voice-message-landing-hero">
          <div className="mx-auto max-w-4xl text-center">
            <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 text-sm text-primary">
              <Sparkles className="h-3.5 w-3.5" />
              {t("landing.newBadge")} {pricingConfig ? `· ${formatPriceWithSign(pricingConfig.voice_message_fee, language)}` : ""}
            </div>
            <h1 className="mt-6 font-heading text-4xl font-medium tracking-tight sm:text-5xl lg:text-6xl">
              {isEn ? "Add your own voice to your song" : "Ajoutez votre propre voix à votre chanson"}
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">
              {isEn
                ? "Record a short sample of your voice, write your personal message, and our AI clones it to create a unique spoken intro or outro for your custom song."
                : "Enregistrez un court échantillon de votre voix, écrivez votre message personnel, et notre IA le clone pour créer une intro ou un outro parlé unique pour votre chanson sur-mesure."}
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              <Link to="/create">
                <Button size="lg" className="rounded-full px-8" data-testid={LANDING.heroCta}>
                  {t("landing.heroCta")}
                </Button>
              </Link>
              <Link to="/dashboard">
                <Button size="lg" variant="outline" className="rounded-full px-8" data-testid="voice-message-landing-profile-cta">
                  {isEn ? "Set up my voice profile" : "Configurer mon profil vocal"}
                </Button>
              </Link>
            </div>
            {!isTwaApp && <p className="mt-4 text-sm text-muted-foreground">{t("landing.heroNote")}</p>}
          </div>
        </section>

        <section className="px-4 py-16 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-6xl">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
              {steps.map((s, i) => (
                <motion.div
                  key={s.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="rounded-2xl border border-border bg-card p-7"
                  data-testid={`voice-message-step-${i + 1}`}
                >
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <s.icon className="h-5 w-5" strokeWidth={1.5} />
                  </div>
                  <h3 className="mt-5 font-heading text-lg font-medium">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 py-16 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-4xl rounded-3xl border border-primary/30 bg-primary/5 p-8 text-center sm:p-12">
            <Heart className="mx-auto h-8 w-8 text-primary" strokeWidth={1.5} />
            <h2 className="mt-4 font-heading text-2xl font-medium tracking-tight sm:text-3xl">
              {isEn ? "The most personal touch you can offer" : "La touche la plus personnelle que vous puissiez offrir"}
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-base">
              {isEn
                ? "Whether it's for a wedding, a birthday, an anniversary or any special occasion, hearing YOUR own voice say a personal message before or after the song creates an emotional moment no generic gift can match."
                : "Que ce soit pour un mariage, un anniversaire ou toute autre occasion spéciale, entendre VOTRE propre voix dire un message personnel avant ou après la chanson crée un moment émouvant qu'aucun cadeau classique ne peut égaler."}
            </p>
            <div className="mt-8">
              <Link to="/create">
                <Button size="lg" className="rounded-full px-8" data-testid="voice-message-landing-bottom-cta">
                  {t("landing.heroCta")}
                </Button>
              </Link>
            </div>
          </div>
        </section>

        <Testimonials />
        <FinalCTA />
      </main>
      <Footer />
    </div>
  );
}
